import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getClients, deleteClient, getClientStats } from '../../utils/clientStorage';
import toast from 'react-hot-toast';

const GeneratedHairstylesGallery = () => {
  const { isDark } = useTheme();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterShape, setFilterShape] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedImage, setSelectedImage] = useState(null);

  // Load clients on component mount
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = () => {
    const clientsData = getClients();
    // Filter only clients with generated hairstyles
    const clientsWithHairstyles = clientsData.filter(client => 
      client.generationData?.outputImage || client.generationData?.outputUrl
    );
    setClients(clientsWithHairstyles);
    setStats(getClientStats());
  };

  const handleDeleteClient = (clientId, clientName) => {
    if (window.confirm(`Are you sure you want to delete ${clientName}'s hairstyle?`)) {
      try {
        deleteClient(clientId);
        loadClients();
        toast.success(`Deleted ${clientName}'s hairstyle`);
      } catch (error) {
        toast.error('Failed to delete client hairstyle');
      }
    }
  };

  // Filter and sort clients
  const filteredClients = clients
    .filter(client => {
      const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           client.phone.includes(searchTerm) ||
                           client.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesShape = !filterShape || client.analysisData?.shape === filterShape;
      return matchesSearch && matchesShape;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'shape':
          return (a.analysisData?.shape || '').localeCompare(b.analysisData?.shape || '');
        default:
          return 0;
      }
    });

  const uniqueShapes = [...new Set(clients.map(c => c.analysisData?.shape).filter(Boolean))];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} py-8`}>
      <div className=" px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Generated Hairstyles Gallery
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            View all AI-generated hairstyles for your clients
          </p>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="text-2xl font-bold text-blue-600">{clients.length}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Generated Hairstyles</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="text-2xl font-bold text-green-600">{stats.withAnalysis}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">With Analysis</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="text-2xl font-bold text-purple-600">{stats.withGeneration}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Generations</div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow">
              <div className="text-2xl font-bold text-orange-600">{stats.recentClients}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">This Week</div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Search Clients
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, phone, or email"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filter by Shape
              </label>
              <select
                value={filterShape}
                onChange={(e) => setFilterShape(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Shapes</option>
                {uniqueShapes.map(shape => (
                  <option key={shape} value={shape}>
                    {shape.charAt(0).toUpperCase() + shape.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name">Name A-Z</option>
                <option value="shape">Face Shape</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={loadClients}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Hairstyles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredClients.map((client) => (
            <div key={client.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              {/* Generated Hairstyle Image */}
              <div className="relative">
                <img
                  src={client.generationData?.outputImage || client.generationData?.outputUrl}
                  alt={`Generated hairstyle for ${client.name}`}
                  className="w-full h-64 object-cover cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => setSelectedImage({
                    src: client.generationData?.outputImage || client.generationData?.outputUrl,
                    name: client.name,
                    shape: client.analysisData?.shape
                  })}
                />
                <div className="absolute top-2 right-2">
                  <span className="bg-green-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                    Generated
                  </span>
                </div>
                <div className="absolute bottom-2 left-2">
                  <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                    {client.analysisData?.shape?.charAt(0).toUpperCase() + client.analysisData?.shape?.slice(1) || 'Unknown'}
                  </span>
                </div>
              </div>

              {/* Client Info */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {client.name}
                </h3>
                
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-2">
                    <span>📅</span>
                    <span>{new Date(client.createdAt).toLocaleDateString()}</span>
                  </div>
                  {client.generationData?.generationTime && (
                    <div className="flex items-center space-x-2">
                      <span>⏱️</span>
                      <span>Generated in {client.generationData.generationTime}s</span>
                    </div>
                  )}
                  {client.generationData?.promptUsed && (
                    <div className="text-xs text-gray-500 truncate">
                      "{client.generationData.promptUsed}"
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 mt-4">
                  <button
                    onClick={() => setSelectedImage({
                      src: client.generationData?.outputImage || client.generationData?.outputUrl,
                      name: client.name,
                      shape: client.analysisData?.shape
                    })}
                    className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    👁️ View Full Size
                  </button>
                  <button
                    onClick={() => handleDeleteClient(client.id, client.name)}
                    className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredClients.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400">
              <div className="text-6xl mb-4">🎨</div>
              <h3 className="text-xl font-semibold mb-2">No generated hairstyles found</h3>
              <p>Generate some hairstyles first, or try adjusting your search or filters</p>
            </div>
          </div>
        )}

        {/* Image Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {selectedImage.name} - Generated Hairstyle
                  </h2>
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    ✕
                  </button>
                </div>

                <div className="text-center">
                  <img
                    src={selectedImage.src}
                    alt={`Generated hairstyle for ${selectedImage.name}`}
                    className="w-full max-w-2xl mx-auto rounded-lg shadow-lg"
                  />
                  <div className="mt-4">
                    <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                      {selectedImage.shape?.charAt(0).toUpperCase() + selectedImage.shape?.slice(1)} Face Shape
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneratedHairstylesGallery;
